import React, { useState } from 'react';
import { useTracksStore } from '../../store/tracks.store';
import { useCollectionCache } from '../../services/collectionCache.service';

function useSortableCounts(refCollection, tracks, field, isArray = false) {
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const counts = (refCollection || []).map(item => {
    const count = tracks.filter(track => {
      const fieldValue = track[field];
      if (!fieldValue) return false;
      if (isArray) {
        return Array.isArray(fieldValue) && fieldValue.some(f => (f.value === item.id || f === item.id));
      } else if (typeof fieldValue === 'object') {
        return fieldValue.value === item.id;
      } else {
        return fieldValue === item.id;
      }
    }).length;
    return { id: item.id, name: item.label || item.name || item.displayName, count };
  });

  const sortedCounts = [...counts].sort((a, b) => {
    if (sortBy === 'name') {
      if (a.name < b.name) return sortDir === 'asc' ? -1 : 1;
      if (a.name > b.name) return sortDir === 'asc' ? 1 : -1;
      return 0;
    } else {
      if (a.count < b.count) return sortDir === 'asc' ? -1 : 1;
      if (a.count > b.count) return sortDir === 'asc' ? 1 : -1;
      return 0;
    }
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  return { sortedCounts, sortBy, sortDir, handleSort };
}

const COLORS = [
  '#fd5d93', '#1d8cf8', '#00f2c3', '#ffb236', '#ff8d72', '#5e72e4', '#2dce89', '#f5365c', '#fb6340', '#11cdef',
  '#8965e0', '#f3a4b5', '#e14eca', '#fbc658', '#51cbce', '#6bd098', '#ef8157', '#b8f7d4', '#f7b8b8', '#b8c6f7',
];

const AnalyticsTable = ({ title, counts = [], sortBy, sortDir, handleSort, totalTracks }) => {
  const maxCount = Math.max(...counts.map(c => c.count), 1);
  return (
    <div style={{ marginTop: 32, minWidth: 350 }}>
      <h4>{title}</h4>
      {/* Total Tracks row */}
      <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 16 }}>
        Total Tracks: {totalTracks}
      </div>
      <table style={{ minWidth: 500 }}>
        <thead>
          <tr>
            <th
              style={{ textAlign: 'left', paddingRight: 16, cursor: 'pointer' }}
              onClick={() => handleSort('name')}
            >
              Name {sortBy === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th
              style={{ textAlign: 'right', cursor: 'pointer' }}
              onClick={() => handleSort('count')}
            >
              Count {sortBy === 'count' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th style={{ width: 180, textAlign: 'left' }}>Bar</th>
            <th style={{ width: 60, textAlign: 'right' }}>%</th>
          </tr>
        </thead>
        <tbody>
          {counts.map(({ id, name, count }, idx) => (
            <tr key={id}>
              <td style={{ textAlign: 'left', paddingRight: 16 }}>{name}</td>
              <td style={{ textAlign: 'right', fontWeight: 'bold', paddingRight: 16 }}>{count}</td>
              <td style={{ width: 180 }}>
                <div style={{
                  height: 18,
                  background: 'transparent',
                  borderRadius: 6,
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                  <div style={{
                    width: `${(count / maxCount) * 100}%`,
                    height: '100%',
                    background: COLORS[idx % COLORS.length],
                    borderRadius: 6,
                    transition: 'width 0.3s',
                  }} />
                </div>
              </td>
              <td style={{ width: 60, textAlign: 'right', fontWeight: 400 }}>
                {totalTracks > 0 ? ((count / totalTracks) * 100).toFixed(1) : '0.0'}%
              </td>
            </tr>
          ))}
          {/* Total row */}
          <tr>
            <td style={{ textAlign: 'left', fontWeight: 700, paddingRight: 16 }}>Total</td>
            <td style={{ textAlign: 'right', fontWeight: 700, paddingRight: 16 }}>
              {counts.reduce((sum, c) => sum + c.count, 0)}
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const SECTION_OPTIONS = [
  { key: 'composers', label: 'Composers' },
  { key: 'agencies', label: 'Agencies' },
  { key: 'clients', label: 'Clients' },
  { key: 'genres', label: 'Genres' },
  { key: 'tags', label: 'Tags' },
];

const Analytics = () => {
  const tracks = useTracksStore((state) => Object.values(state.tracks));
  const totalTracks = tracks.length;
  const composers = useCollectionCache((state) => state.composers || []);
  const agencies = useCollectionCache((state) => state.agency || []);
  const clients = useCollectionCache((state) => state.client || []);
  const genres = useCollectionCache((state) => state.genres || []);
  const tags = useCollectionCache((state) => state.tags || []);

  const composerTable = useSortableCounts(composers, tracks, 'composer', true);
  const agencyTable = useSortableCounts(agencies, tracks, 'agency', false);
  const clientTable = useSortableCounts(clients, tracks, 'client', false);
  const genreTable = useSortableCounts(genres, tracks, 'genre', false);
  const tagTable = useSortableCounts(tags, tracks, 'tags', true);

  const [selectedSection, setSelectedSection] = useState('composers');

  let sectionContent;
  if (selectedSection === 'composers') {
    sectionContent = <AnalyticsTable title="Composers" counts={composerTable.sortedCounts} sortBy={composerTable.sortBy} sortDir={composerTable.sortDir} handleSort={composerTable.handleSort} totalTracks={totalTracks} />;
  } else if (selectedSection === 'agencies') {
    sectionContent = <AnalyticsTable title="Agencies" counts={agencyTable.sortedCounts} sortBy={agencyTable.sortBy} sortDir={agencyTable.sortDir} handleSort={agencyTable.handleSort} totalTracks={totalTracks} />;
  } else if (selectedSection === 'clients') {
    sectionContent = <AnalyticsTable title="Clients" counts={clientTable.sortedCounts} sortBy={clientTable.sortBy} sortDir={clientTable.sortDir} handleSort={clientTable.handleSort} totalTracks={totalTracks} />;
  } else if (selectedSection === 'genres') {
    sectionContent = <AnalyticsTable title="Genres" counts={genreTable.sortedCounts} sortBy={genreTable.sortBy} sortDir={genreTable.sortDir} handleSort={genreTable.handleSort} totalTracks={totalTracks} />;
  } else if (selectedSection === 'tags') {
    sectionContent = <AnalyticsTable title="Tags" counts={tagTable.sortedCounts} sortBy={tagTable.sortBy} sortDir={tagTable.sortDir} handleSort={tagTable.handleSort} totalTracks={totalTracks} />;
  }

  return (
    <div className="pt-3">
      <div style={{ margin: '24px 0 16px 0', display: 'flex', gap: 12 }}>
        {SECTION_OPTIONS.map(opt => (
          <button
            key={opt.key}
            onClick={() => setSelectedSection(opt.key)}
            className={`btn btn-primary-custom btn-round admin-nav-btn admin-analytics-tab-btn${selectedSection === opt.key ? ' selected' : ''}`}
            type="button"
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'row', gap: 32, alignItems: 'flex-start' }}>
        {sectionContent}
      </div>
    </div>
  );
};

export default Analytics; 